import axios, { AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import { useMemo } from 'react';

import { useAuth } from '@/state/auth';
import { useOsintContext } from '@/state/OsintContext';

const axiosInstance = axios.create({
  headers: {
    'Content-Type': 'application/json',
  },
});

const axiosInstanceNotImpersonated = axios.create({
  headers: {
    'Content-Type': 'application/json',
  },
});

export const useAxios = ({
  doNotImpersonate,
  email,
}: {
  doNotImpersonate?: boolean;
  email?: string;
} = {}) => {
  // Resolve doNotImpersonate: explicit prop takes precedence, then OSINT context
  const { isOsint } = useOsintContext();
  const resolvedDoNotImpersonate = doNotImpersonate ?? isOsint;

  const baseInstance = resolvedDoNotImpersonate
    ? axiosInstanceNotImpersonated
    : axiosInstance;

  // If no email, return base instance directly
  if (!email) {
    return baseInstance;
  }

  // If email is provided, create a wrapper instance that adds the account header per-request
  // This avoids mutating the shared base instance and prevents conflicts between components
  /* eslint-disable @typescript-eslint/no-explicit-any */
  const wrapper = {
    ...baseInstance,
    request: (config: any) => {
      return baseInstance.request({
        ...config,
        headers: { ...config?.headers, account: email },
      });
    },
    get: (url: string, config?: any) => {
      return baseInstance.get(url, {
        ...config,
        headers: { ...config?.headers, account: email },
      });
    },
    post: (url: string, data?: any, config?: any) => {
      return baseInstance.post(url, data, {
        ...config,
        headers: { ...config?.headers, account: email },
      });
    },
    put: (url: string, data?: any, config?: any) => {
      return baseInstance.put(url, data, {
        ...config,
        headers: { ...config?.headers, account: email },
      });
    },
    patch: (url: string, data?: any, config?: any) => {
      return baseInstance.patch(url, data, {
        ...config,
        headers: { ...config?.headers, account: email },
      });
    },
    delete: (url: string, config?: any) => {
      return baseInstance.delete(url, {
        ...config,
        headers: { ...config?.headers, account: email },
      });
    },
  } as AxiosInstance;
  /* eslint-enable @typescript-eslint/no-explicit-any */

  return wrapper;
};

export function useInitAxiosInterceptors() {
  const { friend, api, getToken } = useAuth();

  // Must be useMemo (not useEffect) to set baseURL synchronously before first render's API calls.
  // Axios instances are module-level singletons; intentional mutation.
  /* eslint-disable react-hooks/immutability */
  useMemo(() => {
    axiosInstanceNotImpersonated.defaults.baseURL = api ?? '';
    axiosInstance.defaults.baseURL = api ?? '';

    axiosInstance.defaults.headers.common['account'] =
      friend !== '' ? friend : undefined;
  }, [friend, api]);
  /* eslint-enable react-hooks/immutability */

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  async function interceptor(config: InternalAxiosRequestConfig<any>) {
    try {
      // Get the current session from Auth
      const token = await getToken();

      // Add the token to the request headers
      config.headers.Authorization = token ? `Bearer ${token}` : '';
    } catch (error) {
      console.error('Error getting token', error);
    }
    return config;
  }

  useMemo(() => {
    const axiosInstanceInterceptor = axiosInstance.interceptors.request.use(
      interceptor,
      error => {
        return Promise.reject(error);
      }
    );

    const axiosInstanceNotImpersonatedInterceptor =
      axiosInstanceNotImpersonated.interceptors.request.use(
        interceptor,
        error => {
          return Promise.reject(error);
        }
      );

    return () => {
      axiosInstance.interceptors.request.eject(axiosInstanceInterceptor);
      axiosInstance.interceptors.request.eject(
        axiosInstanceNotImpersonatedInterceptor
      );
    };
  }, []);
}
